import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { postRequest } from "../../utils/api";
import { saveToken } from "../../utils/auth";
import OtpStep from "./components/OtpStep";
import "./login.css";

const loginSchema = yup.object().shape({
    username: yup.string().required("Email address is required"),
    password: yup.string().required("Password is required"),
});

const Login = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [capsOn, setCapsOn] = useState(false);
    const [loginError, setLoginError] = useState("");
    // step 2 (email OTP): token from /login is held here and only saved after the code step
    const [pending, setPending] = useState<{ token: string; email: string } | null>(null);

    const { register, handleSubmit, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(loginSchema),
    });

    const onSubmit = async (data: any) => {
        setLoading(true);
        setLoginError("");
        try {
            const response = await postRequest('/login', data);
            if (response.status === 'success') {
                setPending({ token: response.admin.user_admin_token, email: response.admin?.user_email || getValues("username") });
            } else {
                setLoginError(response.message || "Login failed");
            }
        } catch (error: any) {
            setLoginError(error.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const completeOtp = () => {
        if (!pending) return;
        saveToken(pending.token);
        toast.success("Login successful!");
        navigate("/dashboard");
    };

    const capsCheck = (e: React.KeyboardEvent<HTMLInputElement>) => {
        setCapsOn(!!e.getModifierState && e.getModifierState("CapsLock"));
    };

    return (
        <section className="au">
            <div className="au-wrap">
                <div className="au-card">
                    
                    {pending ? (
                        <OtpStep email={pending.email} onVerified={completeOtp} onBack={() => setPending(null)} />
                    ) : (<>
                    <div className="text-center">
                     <img src="/uomox-logo.png" alt="UOMOX" className="w-125p mb-3" />
                    <h2>Sign in to your account</h2>
                    <p className="sub">Access your admin dashboard to manage operations</p>
                    </div>

                    {loginError && (
                        <div className="au-alert"><i className="ri-error-warning-line"></i><div>{loginError}</div></div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className={`au-f ${errors.username ? "err" : ""}`}>
                            <label htmlFor="username">Email address</label>
                            <div className="au-in">
                                <i className="ri-mail-line"></i>
                                <input
                                    id="username"
                                    type="text"
                                    placeholder="Enter email or username"
                                    autoComplete="username"
                                    {...register("username")}
                                />
                            </div>
                            {errors.username && <div className="au-err"><i className="ri-error-warning-line"></i>{errors.username.message}</div>}
                        </div>

                        <div className={`au-f ${errors.password ? "err" : ""}`}>
                            <label htmlFor="password">Password</label>
                            <div className="au-in">
                                <i className="ri-lock-line"></i>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    onKeyUp={capsCheck}
                                    {...register("password")}
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} title={showPassword ? "Hide password" : "Show password"}>
                                    <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"}></i>
                                </button>
                            </div>
                            {errors.password && <div className="au-err"><i className="ri-error-warning-line"></i>{errors.password.message}</div>}
                            {capsOn && <div className="au-caps"><i className="ri-keyboard-line"></i>Caps Lock is on</div>}
                        </div>

                        <div className="au-row">
                            <label><input type="checkbox" defaultChecked />Keep me logged in</label>
                        </div>

                        <button type="submit" className="au-btn" disabled={loading}>
                            {loading && <span className="au-spin"></span>}
                            <span>{loading ? "Signing in…" : "Continue"}</span>
                            {!loading && <i className="ri-arrow-right-line"></i>}
                        </button>
                    </form>
                    </>)}
                </div>

                <div className="au-bottom"><i className="ri-shield-keyhole-line"></i> Restricted area · UOMOX staff only · © {new Date().getFullYear()}</div>
            </div>
        </section>
    );
};

export default Login;
